import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../../services/authService';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  IconButton,
  Button,
} from '@mui/material';
import StepSidebar from '../../../components/StepSidebar';
import JourneyToolbar from '../../../components/JourneyToolbar';
import StepPalette from '../../../components/StepPalette';
import JourneyCanvas from '../../../components/JourneyCanvas';
import { ChevronRight as ChevronRightIcon, ChevronLeft as ChevronLeftIcon, Add as AddIcon } from '@mui/icons-material';
import { useDrop } from 'react-dnd';
import AddJourneyStepDialog from '../../../components/AddJourneyStepDialog';

const JourneyBuilderPage = () => {
  const { journeyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [journey, setJourney] = useState(null);
  const [steps, setSteps] = useState([]);
  const [connections, setConnections] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionSource, setConnectionSource] = useState(null);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [pendingStepType, setPendingStepType] = useState(null);

  // Get funnel data from navigation state
  const funnelData = location.state?.funnel;

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'JOURNEY_STEP',
    drop: (item) => {
      handleAddStep(item.type);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  useEffect(() => {
    if (journeyId) {
      fetchJourneyData();
    }
  }, [journeyId]);

  const fetchJourneyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch journey details
      const journeyRes = await api.get(`/acs/journeys/${journeyId}/`);
      // Merge funnel data from navigation state with journey data
      const journeyWithFunnel = {
        ...journeyRes.data,
        funnel: funnelData?.id || journeyRes.data.funnel,
        funnel_data: funnelData
      };
      setJourney(journeyWithFunnel);

      // Fetch steps
      const stepRes = await api.get(`/acs/journeys/${journeyId}/steps/`);
      setSteps(stepRes.data);

      // Fetch all connections
      const connectionsRes = await api.get('/acs/journey-step-connections/', {
        params: { journey: journeyId }
      });
      setConnections(connectionsRes.data);
    } catch (err) {
      console.error('Error fetching journey data:', err);
      setError('Failed to load journey data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = async (stepType) => {
    setPendingStepType(stepType);
    setIsConfigDialogOpen(true);
  };

  const handleStepConfigSave = async (stepData) => {
    try {
      setSaving(true);
      setIsAddingStep(true);
      
      // Fetch latest steps data before proceeding
      const stepRes = await api.get(`/acs/journeys/${journeyId}/steps/`);
      const currentSteps = stepRes.data;
      
      // Calculate next order based on fetched steps
      const maxOrder = currentSteps.length > 0 
        ? Math.max(...currentSteps.map(step => step.order))
        : 0;
      const nextOrder = maxOrder + 1;
      
      // Create the request payload with the correct order and configuration
      const payload = {
        journey: journeyId,
        name: stepData.name,
        order: nextOrder,
        step_type: stepData.step_type,
        template: stepData.template,
        config: stepData.config,
        is_entry_point: currentSteps.length === 0
      };
      
      const res = await api.post('/acs/journey-steps/', payload);

      // Update steps state with the new step
      const newStep = {
        ...res.data,
        order: nextOrder
      };

      // Update steps state with both existing and new step
      setSteps([...currentSteps, newStep]);

      // Refresh all journey data to ensure consistency
      await fetchJourneyData();
    } catch (err) {
      console.error('Error adding step:', err);
      setError('Failed to add step');
    } finally {
      setSaving(false);
      setIsAddingStep(false);
      setPendingStepType(null);
    }
  };

  const handleUpdateStep = async (stepId, updatedStep) => {
    try {
      const res = await api.put(`/acs/journey-steps/${stepId}/`, updatedStep);
      setSteps(prev => prev.map(s => s.id === stepId ? res.data : s));
      if (selectedStep?.id === stepId) {
        setSelectedStep(res.data);
      }
    } catch (err) {
      console.error('Error updating step:', err);
      setError('Failed to update step');
    }
  };

  const handleAddConnection = async (fromStepId, toStepId, config = {}) => {
    try {
      // Ensure both step IDs are numbers
      const sourceStepId = parseInt(fromStepId);
      const targetStepId = parseInt(toStepId);

      if (isNaN(sourceStepId) || isNaN(targetStepId)) {
        throw new Error('Invalid step IDs provided');
      }

      // Ensure we're sending the correct data structure
      const connectionData = {
        from_step: sourceStepId,
        to_step: targetStepId,
        trigger_type: config.trigger_type || 'immediate',
        priority: config.priority || 1,
        condition_label: config.condition_label,
        delay_duration: config.delay_duration,
        delay_unit: config.delay_unit,
        funnel_step: config.funnel_step,
        event_type: config.event_type,
        condition_config: config.condition_config
      };

      const res = await api.post('/acs/journey-step-connections/', connectionData);
      setConnections(prev => [...prev, res.data]);
    } catch (err) {
      console.error('Error adding connection:', err);
      setError('Failed to add connection');
    }
  };

  const handleEditConnection = async (connectionId, updates) => {
    try {
      console.log('JourneyBuilderPage - Editing Connection:', {
        connectionId,
        updates,
        hasFromStep: !!updates.from_step,
        hasToStep: !!updates.to_step,
        fromStepType: typeof updates.from_step,
        toStepType: typeof updates.to_step
      });

      // Ensure we have all required fields
      if (!updates.from_step || !updates.to_step) {
        console.error('Missing required fields:', {
          from_step: updates.from_step,
          to_step: updates.to_step,
          updates
        });
        throw new Error('Missing required fields: from_step and to_step');
      }

      // Ensure step IDs are numbers
      const connectionData = {
        ...updates,
        from_step: parseInt(updates.from_step),
        to_step: parseInt(updates.to_step)
      };

      console.log('JourneyBuilderPage - Sending connection update:', {
        connectionId,
        connectionData
      });

      const res = await api.put(`/acs/journey-step-connections/${connectionId}/`, connectionData);
      console.log('JourneyBuilderPage - Connection Update Response:', {
        status: res.status,
        data: res.data
      });
      
      setConnections(prev => prev.map(c => c.id === connectionId ? res.data : c));
    } catch (err) {
      console.error('Error editing connection:', {
        error: err,
        message: err.message,
        updates
      });
      setError('Failed to edit connection');
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    try {
      await api.delete(`/acs/journey-step-connections/${connectionId}/`);
      setConnections(prev => prev.filter(c => c.id !== connectionId));
    } catch (err) {
      console.error('Error deleting connection:', err);
      setError('Failed to delete connection');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;
    
    try {
      setSaving(true);
      await api.delete(`/acs/journey-steps/${stepId}/`);
      setSteps(steps.filter(s => s.id !== stepId));
      setConnections(connections.filter(c => c.from_step !== stepId && c.to_step !== stepId));
      setSelectedStep(null);
    } catch (err) {
      console.error('Error deleting step:', err);
      setError('Failed to delete step');
    } finally {
      setSaving(false);
    }
  };

  const handleStartConnection = (stepId, position) => {
    setIsConnecting(true);
    setConnectionSource(stepId);
  };

  const handleCompleteConnection = (targetStepId) => {
    if (connectionSource && targetStepId !== connectionSource) {
      handleAddConnection(connectionSource, targetStepId);
    }
    setIsConnecting(false);
    setConnectionSource(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!journey) {
    return (
      <Box p={3}>
        <Alert severity="error">Journey not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <JourneyToolbar 
        journeyId={journeyId} 
        journeyName={journey?.name}
        onSave={fetchJourneyData}
        saving={saving}
      />
      
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <StepPalette />
        
        <Box 
          ref={drop}
          className="canvas-container"
          sx={{ 
            flex: 1, 
            position: 'relative', 
            overflow: 'auto',
            bgcolor: isOver ? 'action.hover' : 'grey.100',
            p: 3,
            transition: 'background-color 0.2s ease',
          }}
        >
          {steps.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 8,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No steps yet
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Drag a step from the palette or click the button below to start building your journey
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsAddingStep(true)}
                sx={{ mt: 2 }}
              >
                Add Step
              </Button>
            </Box>
          ) : (
            <JourneyCanvas
              steps={steps}
              connections={connections}
              selectedStep={selectedStep}
              onSelectStep={setSelectedStep}
              onAddStep={handleAddStep}
              onUpdateStep={handleUpdateStep}
              onAddConnection={handleAddConnection}
              onEditConnection={handleEditConnection}
              onDeleteConnection={handleDeleteConnection}
              onDeleteStep={handleDeleteStep}
              isConnecting={isConnecting}
              connectionSource={connectionSource}
              onStartConnection={handleStartConnection}
              onCompleteConnection={handleCompleteConnection}
              journey={journey}
            />
          )}
        </Box>
        
        <Box 
          sx={{ 
            position: 'relative',
            width: isSidebarCollapsed ? '40px' : '300px',
            borderLeft: 1, 
            borderColor: 'divider',
            transition: 'width 0.3s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconButton
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover',
              },
              zIndex: 1,
            }}
          >
            {isSidebarCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
          
          {!isSidebarCollapsed && (
            <StepSidebar
              step={selectedStep}
              onUpdate={handleUpdateStep}
              onDelete={handleDeleteStep}
              saving={saving}
              steps={steps}
              connections={connections}
              onConnectTo={handleAddConnection}
              onEditConnection={handleEditConnection}
              onDeleteConnection={handleDeleteConnection}
              journey={journey}
            />
          )}
        </Box>
      </Box>
      
      <AddJourneyStepDialog
        open={isConfigDialogOpen}
        onClose={() => {
          setIsConfigDialogOpen(false);
          setPendingStepType(null);
        }}
        onSave={handleStepConfigSave}
        stepType={pendingStepType}
        initialName={`Step ${steps.length + 1}`}
      />
    </Box>
  );
};

export default JourneyBuilderPage; 